import { Component, ViewChild, OnInit } from '@angular/core';
import { Observable, timer } from 'rxjs';
import { StatusBar } from '../statusbar/statusbar.component';
import { GoogleMapComponent } from '../google-map/google-map.component';
import { takeWhile } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BusInfo, Colour, Coordinate, emvHeartbeatInfo, HeartbeatLocation, MapModel, Status, TripInfo } from 'src/app/app.model';
import { StateService } from 'src/app/Service/state-service.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { isWithinLast } from '../../Utils/date-utils';

// DO NOT REMOVE. This is reference to the map initialised by GoogleMapComponent
/**
 * @see {@link GoogleMapComponent}
 */
declare var google: { maps: any };

@Component({
  selector: 'vehicle-state',
  templateUrl: './vehiclestate.component.html',
  styleUrls: ['./vehiclestate.component.scss'],
  imports: [
    StatusBar,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    GoogleMapComponent
  ]
})
export class VehicleState implements OnInit {
  @ViewChild(GoogleMapComponent, { static: true }) public googleMapsComponent: GoogleMapComponent;

  vehicleID = new FormControl('', [Validators.required]);
  errorMessage: string = '';
  isLoading = false;
  hasData = false;
  tripInformation: TripInfo = undefined;
  status: Status = undefined;
  emvHeartbeats: emvHeartbeatInfo[] = undefined;

  mapModel: MapModel = {};
  showMapView = false;

  /** Polling fields */
  private _pollingInterval: Observable<number>;
  private _shouldPoll: boolean = false;
  private _bdcHeartbeat: Coordinate;
  private _cradlepointHeartbeat: Coordinate;
  private _submittedVehicleId: string = '';

  // initial center position for the map
  public lat: number = -41.034;
  public lng: number = 175.519;

  constructor(private _stateSvc: StateService) {}

  ngOnInit() {
    this.googleMapsComponent.restrictBoundsToNZ(true);
  }

  submit() {
    if (this.vehicleID.valid && this.vehicleID.value) {
      this._submittedVehicleId = this.vehicleID.value;
      this.hasData = true;
      this._resetVehicleInfo();
      this._getHeartBeat(true, true, 'Error: No information for vehicle ID. Please check details and try again.');
    } else {
      this.errorMessage = 'Please enter a valid Vehicle ID';
      this.hasData = false;
    }
  }

  private _resetVehicleInfo() {
    this.showMapView = false;
    this._bdcHeartbeat = undefined;
    this._cradlepointHeartbeat = undefined;
    this.status = undefined;
    this.errorMessage = '';
  }

  startPolling() {
    if (!this._shouldPoll) {
      this._shouldPoll = true;
      this._pollingInterval = timer(0, 5000);
      this._pollingInterval
        .pipe(
          takeWhile(() => {
            return this._shouldPoll;
          })
        )
        .subscribe(() => {
          this._getHeartBeat(false, false, 'Failed to get heartbeat. Please refresh the page.');
        });
    } else {
      // Already polling, no need to start.
    }
  }

  private _getHeartBeat(initialLoad: boolean, startPolling: boolean, errorMsg: string) {
    this.isLoading = initialLoad;

    this._stateSvc
      .getHeartbeat(this._submittedVehicleId)
      .then((busInfo) => {
        this.errorMessage = '';

        // set status info
        this.tripInformation = busInfo.tripInfo;
        this.status = busInfo.status;
        this.emvHeartbeats = busInfo.emvHeartbeats;

        // set map info
        this._bdcHeartbeat = this._setBdcHeartbeat(busInfo.bdcCoordinate);
        this._cradlepointHeartbeat = this._setCpHeartbeat(busInfo.cpCoordinate);
        if (busInfo.emvHeartbeats && busInfo.emvHeartbeats.length > 0) {
          this._setEmvHeartbeatColor(busInfo.emvHeartbeats);
        }

        // set map
        this._setMapModel();
        this.showMapView = true;

        if (startPolling) {
          this.startPolling();
        }
      })
      .catch((error) => {
        this.errorMessage = errorMsg;
        this._shouldPoll = false;
        if (initialLoad) {
          this.hasData = false;
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private _setMapModel() {
    let heartbeats: HeartbeatLocation[] = [];
    if (this._bdcHeartbeat !== null && this._bdcHeartbeat !== undefined) {
      heartbeats[heartbeats.length] = {
        lat: this._bdcHeartbeat.latitude,
        lng: this._bdcHeartbeat.longitude,
        cplat: null,
        cplng: null,
        direction: this._bdcHeartbeat.direction,
        cpDirection: null,
        iconPath: this._bdcHeartbeat.iconPath,
        time: null,
        color: this._bdcHeartbeat.color,
        rad: this._bdcHeartbeat.rad
      };
    }
    if (this._cradlepointHeartbeat !== null && this._cradlepointHeartbeat !== undefined) {
      heartbeats[heartbeats.length] = {
        lat: null,
        lng: null,
        cplat: this._cradlepointHeartbeat.latitude,
        cplng: this._cradlepointHeartbeat.longitude,
        direction: null,
        cpDirection: this._cradlepointHeartbeat.direction,
        iconPath: this._cradlepointHeartbeat.iconPath,
        time: null,
        color: this._cradlepointHeartbeat.color,
        rad: this._cradlepointHeartbeat.rad
      };
    }
    this.mapModel = {
      centerLat: this.lat,
      centerLng: this.lng,
      markers: heartbeats,
      scheduledLine: {
        scheduledCoords: null,
        strokeColor: 'grey',
        strokeWeight: 5
      },
      stops: null
    };
  }

  private _setCpHeartbeat(heartbeat: Coordinate) {
    let cpHeartbeat = heartbeat;

    if (cpHeartbeat !== null && cpHeartbeat !== undefined) {
      let colourState;
      if (this.status.cpColour == Colour.GREEN) {
        colourState = 'green';
      } else if (this.status.cpColour == Colour.ORANGE) {
        colourState = 'orange';
      } else if (this.status.cpColour == Colour.RED) {
        colourState = 'red';
      } else if (this.status.cpColour == Colour.BLUE) {
        colourState = 'blue';
      } else {
        colourState = 'grey';
      }

      let icon = {
        url: '/assets/svg/cp_' + colourState + '.svg',
        size: new google.maps.Size(24, 24),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(12, 12)
      };

      cpHeartbeat.iconPath = icon;
      cpHeartbeat.rad = 10;
    }

    return cpHeartbeat;
  }

  private _setBdcHeartbeat(heartbeat: Coordinate) {
    let bdcHeartbeat = heartbeat;

    if (bdcHeartbeat !== null && bdcHeartbeat !== undefined) {
      let direction: number = (Math.round((bdcHeartbeat.direction - 5) / 10.0) * 10) % 360;

      let colourState;
      if (this.status.bdcColour == Colour.GREEN) {
        colourState = 'good';
      } else if (this.status.bdcColour == Colour.ORANGE) {
        colourState = 'reverse';
      } else {
        colourState = 'bad';
      }

      let icon = {
        url: '/assets/svg/nav-arrow-' + colourState + '/' + colourState + '_' + direction + '.svg',
        size: new google.maps.Size(24, 24),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(12, 12)
      };

      bdcHeartbeat.iconPath = icon;
      bdcHeartbeat.rad = 10;
    }

    return bdcHeartbeat;
  }

  private _setEmvHeartbeatColor(emvHeartbeats: emvHeartbeatInfo[]) {
    let successCount = 0;
    const ONE_HOUR = 60 * 60 * 1000;

    for (const hb of emvHeartbeats) {
      const isValid = hb.aipaServerStatus === '1' && hb.aipaUpdateStatus === '1' && isWithinLast(hb.time, ONE_HOUR);

      if (isValid) {
        successCount++;
      }
    }

    if (successCount === emvHeartbeats.length && successCount > 0) {
      this.status.emvColour = Colour.GREEN;
    } else if (successCount > 0) {
      this.status.emvColour = Colour.ORANGE;
    } else {
      this.status.emvColour = Colour.RED;
    }
  }

  private _isTimeLessThanOneHourOld(timeStr: string): boolean {
    const heartbeatTime = new Date(timeStr);
    const now = new Date();
    const diffMs = now.getTime() - heartbeatTime.getTime();
    return diffMs < 60 * 60 * 1000;
  }
}
